import React from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@material-ui/core/Button';
import Skeleton from '@material-ui/lab/Skeleton';

/**
 * Shared layout shell for all report pages.
 *
 * Props:
 *   title        {string}   – report heading
 *   icon         {string}   – material-icons ligature
 *   summaryTiles {Array}    – [{ label, value, icon, className }]
 *   filters      {node}     – JSX for the filter bar
 *   table        {node}     – JSX for the data table / content
 *   isLoading    {boolean}
 *   error        {string|null}
 *   data         {Array}    – rows (used for empty-state check)
 *   onBack       {function} – navigate back to reports grid
 *   onDownload   {function} – trigger PDF download
 */
function ReportShell({title, icon, summaryTiles, filters, table, isLoading, error, data, onBack, onDownload, extraActions}) {
  return (
    <div className="dd-report-page">
      {/* Back button */}
      <a href="#" onClick={(e) => {
        e.preventDefault(); onBack();
      }} className="back-link">
        <i className="material-icons">arrow_back</i>
        Back
      </a>

      {/* Header row */}
      <div className="dd-report-page-header">
        <div className="dd-report-page-title">
          <h2>
            <i className="material-icons dd-report-title-icon">{icon || 'description'}</i>
            {title}
          </h2>
        </div>
        <Button
          variant="contained"
          color="primary"
          startIcon={<i className="material-icons">file_download</i>}
          onClick={onDownload}
          className="dd-report-download-btn"
        >
          Download PDF
        </Button>
      </div>

      {/* Extra actions (e.g. refresh button for usage report) */}
      {extraActions && (
        <div className="dd-report-extra-actions">
          {extraActions}
        </div>
      )}

      {/* Filter bar — above the tiles (#626). You narrow the report first, then read the numbers
          the narrowing produced; tiles-first invited reading totals that the filter then changed. */}
      {filters && (
        <div className="dd-report-filter-bar">
          {filters}
        </div>
      )}

      {/* Summary tiles row */}
      {summaryTiles && summaryTiles.length > 0 && (
        <div className="dd-report-summary-tiles">
          {summaryTiles.map((tile, idx) => (
            <div
              key={idx}
              className={`stat-card ${tile.className || ''}${tile.isActive ? ' stat-card-active' : ''}`}
              onClick={tile.onClick}
              style={{cursor: tile.onClick ? 'pointer' : 'default'}}
            >
              <div className="stat-label">{tile.label}</div>
              {/* Skeleton while loading (#627). The tiles derive from the pre-fetch Redux `data`,
                  so without this they render a confident "0" the instant the report mounts — a real
                  value the user has no way to tell from an empty one. (The loaded state below shows
                  genuine 0s, which is exactly why the loading state must not.)

                  Inside .stat-value rather than replacing it, so the element keeps its place in the
                  layout. height 24 is measured, not guessed: .stat-value renders 24px tall, and the
                  MUI default made the tile 8px taller while loading, so every tile visibly shrank
                  when the numbers landed. */}
              <div className="stat-value">
                {isLoading ? <Skeleton variant="text" width={56} height={24} /> : tile.value}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Content area */}
      {isLoading ? (
        <div className="dd-view-loading">
          <CircularProgress />
          <p>Loading report…</p>
        </div>
      ) : error ? (
        <div className="dd-view-empty">
          <i className="material-icons">error_outline</i>
          <h3>Failed to load report</h3>
          <p>{error}</p>
        </div>
      ) : (!data || data.length === 0) ? (
        <div className="dd-view-empty">
          <i className="material-icons">inbox</i>
          <h3>No data available</h3>
          <p>No records match the current filters.</p>
        </div>
      ) : (
        table
      )}
    </div>
  );
}

export default ReportShell;
